import React, { useState, useEffect } from 'react';
import './TasksPage.css';
import QuickStatsRow from './components/QuickStatsRow/QuickStatsRow';
import ContactInsights from './components/ContactInsights/ContactInsights';
import TaskHistoryList from './components/TaskHistoryList/TaskHistoryList';
import TaskTimer from './components/TaskTimer/TaskTimer';

function TasksPage({ user, onBack }) {
  const [stats, setStats] = useState(null);
  const [activeStage, setActiveStage] = useState('all');
  const [dateRange, setDateRange] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [taskHistory, setTaskHistory] = useState([]);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);

  const stages = [
    { key: 'all', name: 'All Tasks', color: '#6b7280', icon: '📋' },
    { key: 'lead', name: 'Leads', color: '#3b82f6', icon: '👥' },
    { key: 'customer', name: 'Customers', color: '#10b981', icon: '🛒' },
    { key: 'marketer', name: 'Marketers', color: '#f59e0b', icon: '🚀' },
    { key: 'leader', name: 'Leaders', color: '#8b5cf6', icon: '👑' }
  ];

  useEffect(() => {
    setHistoryOffset(0); // Reset offset when filters change
    fetchStats();
    fetchPendingTasks();
  }, [activeStage, dateRange]);

  useEffect(() => {
    fetchTaskHistory(historyOffset > 0); // Append if offset > 0
  }, [activeStage, dateRange, historyOffset]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`/backend/api/tasks/task-stats.php?stage=${activeStage}&date_range=${dateRange}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingTasks = async () => {
    try {
      const response = await fetch('/backend/api/tasks/todays-tasks.php', {
        credentials: 'include'
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.success) {
        // Filter by active stage
        if (activeStage === 'all') {
          setPendingTasks(data.tasks);
        } else {
          setPendingTasks(data.tasks.filter(t => t.stage === activeStage));
        }
      }
    } catch (error) {
      console.error('Error fetching pending tasks:', error);
    }
  };

  const fetchTaskHistory = async (append = false) => {
    try {
      const response = await fetch(
        `/backend/api/tasks/task-history.php?stage=${activeStage}&date_range=${dateRange}&limit=20&offset=${historyOffset}`,
        { credentials: 'include' }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.success) {
        if (append) {
          setTaskHistory(prev => [...prev, ...data.history]);
        } else {
          setTaskHistory(data.history);
        }
        setHasMoreHistory(data.pagination.has_more);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const loadMoreHistory = () => {
    setHistoryOffset(prev => prev + 20);
  };

  const handleTaskComplete = async (taskId, action) => {
    try {
      const response = await fetch('/backend/api/tasks/complete-task.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ task_id: taskId, action: action })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.success) {
        // Refresh all data
        fetchStats();
        fetchPendingTasks();
        fetchTaskHistory();
      }
    } catch (error) {
      console.error('Error completing task:', error);
      alert('Failed to update task');
    }
  };

  if (loading) {
    return (
      <div className="tasks-page">
        <div className="loading-spinner">Loading tasks...</div>
      </div>
    );
  }

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <button className="back-btn" onClick={onBack}>← Back to Dashboard</button>
        <h1>Tasks Management</h1>
      </div>

      {stats && <QuickStatsRow stats={stats} dateRange={dateRange} />}

      <div className="filter-bar">
        <div className="date-filters">
          <button 
            className={`filter-btn ${dateRange === '7d' ? 'active' : ''}`}
            onClick={() => setDateRange('7d')}
          >
            Last 7 Days
          </button>
          <button 
            className={`filter-btn ${dateRange === '30d' ? 'active' : ''}`}
            onClick={() => setDateRange('30d')}
          >
            Last 30 Days
          </button>
          <button 
            className={`filter-btn ${dateRange === 'all' ? 'active' : ''}`}
            onClick={() => setDateRange('all')}
          >
            All Time
          </button>
        </div>

        <div className="stage-tabs">
          {stages.map((stage) => (
            <button
              key={stage.key}
              className={`stage-tab ${activeStage === stage.key ? 'active' : ''}`}
              onClick={() => setActiveStage(stage.key)}
              style={{ '--stage-color': stage.color }}
            >
              <span className="stage-icon">{stage.icon}</span>
              <span className="stage-name">{stage.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="tasks-content">
        <div className="section pending-tasks-section">
          <h2>Today's Pending Tasks ({pendingTasks.length})</h2>
          <div className="task-legend">
            <span className="legend-icon">ℹ️</span>
            <span className="legend-text">
              Tasks with <strong>✓</strong> were completed before they appeared here - great job staying ahead!
            </span>
          </div>
          {pendingTasks.length > 0 ? (
            <div className="pending-tasks-list">
              {pendingTasks.map((task) => (
                <div key={task.id} className={`task-item ${task.is_overdue ? 'overdue' : ''}`}>
                  <div className="task-content">
                    <span className="task-contact">{task.contact_name || 'General Task'}</span>
                    <span className="task-description">{task.description}</span>
                    {task.is_overdue && <span className="overdue-badge">OVERDUE</span>}
                  </div>
                  {task.deadline_timestamp && (
                    <TaskTimer 
                      deadlineTimestamp={task.deadline_timestamp} 
                      compact={false}
                      completedBeforeGeneration={task.completed_before_generation}
                    />
                  )}
                  <div className="task-actions">
                    <button 
                      className="task-complete-btn"
                      onClick={() => handleTaskComplete(task.id, 'complete')}
                    >
                      ✓
                    </button>
                    <button 
                      className="task-dismiss-btn"
                      onClick={() => handleTaskComplete(task.id, 'dismiss')}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">No pending tasks</div>
          )}
        </div>

        <ContactInsights user={user} dateRange={dateRange} stage={activeStage} />

        <TaskHistoryList 
          history={taskHistory} 
          onLoadMore={loadMoreHistory} 
          hasMore={hasMoreHistory}
        />
      </div>
    </div>
  );
}

export default TasksPage;
